import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getLesson, viewLesson, joinLesson, getSections } from '../../services/studentService';
import toast from 'react-hot-toast';
import LessonPager from './LessonPager';
import VideoPlayer from '../Common/VideoPlayer';
import { FileIcon, fileColor, fileMeta } from '../Common/fileKind';

// ── Join button component ──────────────────────────────────────────────────
// Ссылка выдаётся только сервером после проверки и берётся из профиля ученика.
// Кнопку показываем всегда: если личной ссылки нет, по клику объясняем почему.
const NO_LINK_MSG = 'У вас не подключена ссылка для входа на занятие — напишите куратору';

// Идёт урок, ещё не начался или уже прошёл. PostgreSQL отдаёт TIME как
// "HH:MM:SS" — режем до "HH:MM", иначе ISO-строка невалидна. МСК = UTC+3.
const phaseOf = (lesson) => {
  if (!lesson?.date || !lesson?.time) return 'none';
  const lessonStart = new Date(`${lesson.date}T${lesson.time.slice(0, 5)}:00+03:00`);
  if (isNaN(lessonStart)) return 'none';
  const now = new Date();
  if (lessonStart - now > 0) return 'upcoming';
  // Длительность урока задаёт админ; не задана → 120 минут
  const durMs = (lesson.duration > 0 ? lesson.duration : 120) * 60 * 1000;
  return now - lessonStart < durMs ? 'live' : 'ended';
};

// Пока урок идёт, вместо «запись появится» показываем, что эфир в самом разгаре
const RecordPlaceholder = ({ lesson }) => {
  const [phase, setPhase] = useState(() => phaseOf(lesson));
  useEffect(() => {
    setPhase(phaseOf(lesson));
    const t = setInterval(() => setPhase(phaseOf(lesson)), 30000);
    return () => clearInterval(t);
  }, [lesson]);

  if (phase !== 'live') {
    return <div className="lesson-video-placeholder">Запись урока появится после проведения</div>;
  }
  return (
    <div className="lesson-video-placeholder live">
      <span className="lv-live-badge"><i />В эфире</span>
      <div className="lv-live-title">Урок идёт прямо сейчас</div>
      <div className="lv-live-sub">Нажмите «Войти в урок» наверху, чтобы подключиться. Запись появится здесь после эфира.</div>
    </div>
  );
};

const JoinButton = ({ lesson }) => {
  const lessonDateTime = () => {
    const t = lesson.time?.slice(0, 5) || '';
    return new Date(`${lesson.date}T${t}:00+03:00`);
  };

  const getPhase = useCallback(() => phaseOf(lesson), [lesson]);

  const getCountdown = useCallback(() => {
    if (!lesson.date || !lesson.time) return '';
    const lessonStart = lessonDateTime();
    if (isNaN(lessonStart)) return '';
    const diff = lessonStart - new Date();
    if (diff <= 0) return '';
    const totalMin = Math.floor(diff / 60000);
    const d = Math.floor(totalMin / 1440);
    const h = Math.floor((totalMin % 1440) / 60);
    const m = totalMin % 60;
    // русские склонения для дней/часов (когда осталось больше суток)
    const plural = (n, a, b, c) => {
      const n10 = n % 10, n100 = n % 100;
      if (n10 === 1 && n100 !== 11) return a;
      if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return b;
      return c;
    };
    if (d > 0) return `${d} ${plural(d, 'день', 'дня', 'дней')}${h > 0 ? ` ${h} ${plural(h, 'час', 'часа', 'часов')}` : ''}`;
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
  }, [lesson]);

  const [phase, setPhase] = useState(() => getPhase());
  const [countdown, setCountdown] = useState(() => getCountdown());
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const tick = () => { setPhase(getPhase()); setCountdown(getCountdown()); };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [getPhase, getCountdown]);

  const handleJoin = async () => {
    // Личной ссылки нет — говорим сразу, не открывая пустую вкладку
    if (!lesson.hasLiveLink) { toast.error(NO_LINK_MSG); return; }
    setJoining(true);
    // Вкладку открываем СИНХРОННО по тапу — иначе мобильные браузеры блокируют
    // окно, открытое после await (теряется «жест пользователя»). Ссылку подставим,
    // когда сервер ответит. Если окно открыть не дали — переходим в той же вкладке.
    const win = window.open('', '_blank');
    try {
      const res = await joinLesson(lesson.id);
      const url = res?.success && res.data?.redirectUrl;
      if (url) {
        if (win && !win.closed) { win.opener = null; win.location.href = res.data.redirectUrl; }
        else window.location.href = res.data.redirectUrl;
      } else {
        if (win && !win.closed) win.close();
        toast.error(res.message || 'Не удалось получить ссылку');
      }
    } catch (err) {
      if (win && !win.closed) win.close();
      toast.error(err?.response?.data?.message || 'Ошибка подключения');
    } finally {
      setJoining(false);
    }
  };

  if (phase === 'none') return null;

  if (phase === 'live') {
    return (
      <button
        onClick={handleJoin}
        disabled={joining}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 20px', border: 'none', borderRadius: 10,
          fontSize: 14, fontWeight: 700, cursor: joining ? 'not-allowed' : 'pointer',
          background: joining ? '#6b7280' : '#16a34a',
          color: 'white', transition: 'background 0.15s',
          boxShadow: '0 2px 8px rgba(22,163,74,0.35)',
          flexShrink: 0,
        }}
      >
        {/* Live dot */}
        {!joining && (
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'white',
            boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
            animation: 'pulse 1.5s infinite',
          }} />
        )}
        {joining ? 'Подключение...' : 'Войти в урок'}
      </button>
    );
  }

  if (phase === 'upcoming') {
    return (
      <button disabled style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '10px 20px', border: '1px solid var(--border)', borderRadius: 10,
        fontSize: 13, fontWeight: 600, cursor: 'not-allowed',
        background: 'rgba(124,77,255,0.08)', color: 'var(--text-2)', flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        Урок через {countdown}
      </button>
    );
  }

  if (phase === 'ended') {
    return (
      <span style={{ fontSize: 13, color: '#94a3b8', padding: '10px 0', flexShrink: 0 }}>
        Урок завершён
      </span>
    );
  }

  return null;
};

const IcArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IcTask = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/><path d="M9 14l2 2 4-4"/>
  </svg>
);

const getEmbedUrl = (url) => {
  if (!url) return '';
  if (url.includes('youtube.com/watch') || url.includes('youtu.be')) {
    const videoId = url.includes('youtu.be') ? url.split('/').pop() : url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('vimeo.com')) return `https://player.vimeo.com/video/${url.split('/').pop()}`;
  if (url.includes('rutube.ru')) return `https://rutube.ru/play/embed/${url.split('/').pop()}`;
  if (url.match(/\.(mp4|webm|ogg)$/i)) return url;
  return url;
};

// Прямой видеофайл (mp4/webm и т.п. или ссылка с Я.Облака / S3) — показываем
// своим плеером. YouTube/Vimeo/Rutube — встроенным iframe сервиса.
const isDirectVideo = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    if (/\.(mp4|webm|ogg|ogv|mov|m4v)$/i.test(u.pathname)) return true;
    if (/(yandexcloud\.net|storage\.yandex|\.s3\.|s3\.|timeweb\.cloud)/i.test(u.hostname)) return true;
  } catch {
    return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|$)/i.test(url);
  }
  return false;
};

/* Страница урока: только сам урок (вход в эфир / запись / материалы).
   Домашнее задание вынесено на отдельную страницу /homework/:id. */
const LessonView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lessonIds, setLessonIds] = useState([]);

  useEffect(() => {
    setLesson(null);
    setLoading(true);
    loadLesson();
  }, [id]);

  /* Соседи для стрелок ← → — ровно тот же порядок, что ученик видит в «Уроках»:
     модули по порядку, внутри модуля по sortOrder. Две поправки к прежнему коду:
     курс берём у самого урока (у ученика их бывает несколько, и соседей надо
     искать в том же курсе), а ДЗ-блоки в список не берём — они живут на своей
     странице /homework/:id, и стрелка уводила на несуществующий вебинар. */
  useEffect(() => {
    const courseId = lesson?.Section?.courseId ?? lesson?.courseId;
    if (!courseId) return;
    getSections(courseId).then(res => {
      const ids = (res.data || [])
        .flatMap(s => (s.Lessons || [])
          .filter(l => !l.isHomeworkOnly && !l.isVariant)
          .slice()
          .sort((a, b) => (a.sortOrder ?? a.sort_order ?? 0) - (b.sortOrder ?? b.sort_order ?? 0)))
        .map(l => l.id);
      setLessonIds(ids);
    }).catch(() => {});
  }, [lesson?.Section?.courseId, lesson?.courseId]);

  const loadLesson = async () => {
    try {
      const response = await getLesson(id);
      setLesson(response.data);
      // Отметка «просмотрен» ставится сервером только если урок прошёл/есть запись
      await viewLesson(id);
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Ошибка загрузки урока');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="lesson-full lesson-loading" style={{ background: 'var(--bg)' }}><div className="lesson-spinner" /></div>;
  if (!lesson) return <div className="lesson-full lesson-loading" style={{ background: 'var(--bg)' }}>Урок не найден</div>;

  const hasHomework = lesson.Homework?.length > 0;
  const embedUrl = lesson.recordLink && !lesson.recordLink.includes('zoom.us') ? getEmbedUrl(lesson.recordLink) : null;

  return (
    <div className="lesson-full" style={{ background: 'var(--bg)' }}>
      <div className="nhw-wrap">
        <div className="nhw-card fixed" style={{ maxWidth: 'none' }}>
          {/* Шапка */}
          <div className="nhw-head">
            <button className="nhw-tb" onClick={() => navigate(location.state?.from || '/lessons')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', border: '1px solid var(--lg-edge)', borderRadius: 10, background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>
              <IcArrow /> Назад
            </button>
            {/* Класс, а не инлайновые стили: на телефоне заголовок должен занимать
                всю ширину и переноситься, а инлайновый flex это правило перебивал */}
            <div className="nhw-head-main">
              <div className="nhw-head-title">{lesson.title}</div>
              {lesson.date && (
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
                  {new Date(lesson.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}{lesson.time ? ` · ${lesson.time.slice(0, 5)}` : ''}
                </div>
              )}
            </div>
            <JoinButton lesson={lesson} />
          </div>

          {/* Тело: статичный верх (видео + «Содержание» + кнопка ДЗ) + прокрутка (материалы) */}
          <div className="lv-body">
            <div className="lv-top">
              {/* Video / recording. Прямое видео — свой плеер (с таймкодами и «Содержанием»);
                  остальное (Zoom/iframe/заглушка) — в обычном боксе. */}
              {lesson.recordLink && !lesson.recordLink.includes('zoom.us') && isDirectVideo(lesson.recordLink) ? (
                <VideoPlayer src={lesson.recordLink} timecodes={lesson.timecodes || []} />
              ) : (
                <div className="lesson-video-box">
                  {lesson.recordLink ? (
                    lesson.recordLink.includes('zoom.us') ? (
                      <div className="lesson-video-placeholder">
                        <a href={lesson.recordLink} target="_blank" rel="noopener noreferrer" className="lesson-zoom-link">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 4 20 12 6 20 6 4"/></svg>
                          Смотреть запись урока (Zoom)
                        </a>
                      </div>
                    ) : (
                      <iframe
                        src={embedUrl}
                        title="Запись урока"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )
                  ) : (
                    <RecordPlaceholder lesson={lesson} />
                  )}
                </div>
              )}

              {/* Переход к ДЗ или варианту — статично под видео */}
              {(() => {
                let target = null, label = 'Перейти к домашнему заданию', tPath = 'homework';
                if (hasHomework) target = { id, count: lesson.Homework.length };
                else if (lesson.linkedVariant?.count > 0) { target = lesson.linkedVariant; label = 'Перейти к варианту'; tPath = 'variant'; }
                else if (lesson.linkedHomework?.count > 0) target = lesson.linkedHomework;
                if (!target) return null;
                return (
                  <>
                  <button
                    className="btn-finish"
                    onClick={() => navigate(`/${tPath}/${target.id}`, { state: { from: `/lesson/${id}` } })}
                    style={{ width: '100%', marginTop: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}
                  >
                    <IcTask />
                    {label}
                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '1px 9px', fontSize: 12 }}>{target.count}</span>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </button>
                  {/* Куратор выдал ещё попытку по привязанному ДЗ/варианту */}
                  {target.extraAttempts > 0 && (
                    <div className="s-attempt center">Доступна ещё одна попытка — засчитается лучший результат</div>
                  )}
                  </>
                );
              })()}
            </div>

            {/* Прокручиваемая часть: материалы + навигация между уроками */}
            <div className="lv-scroll">
              {lesson.materials?.length > 0 && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.2px', color: 'var(--text-1)', marginBottom: 12 }}>Материалы</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {lesson.materials.map((material, i) => {
                      // Локальные файлы (/uploads) скачиваем под настоящим именем через ?name;
                      // на проде имя уже задаёт S3.
                      const href = material.url?.includes('/uploads/')
                        ? `${material.url}?name=${encodeURIComponent(material.filename || 'file')}`
                        : material.url;
                      return (
                        <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 15, padding: '14px 16px', textDecoration: 'none', transition: 'border-color .15s, box-shadow .15s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.4)'; e.currentTarget.style.boxShadow = '0 12px 28px -18px rgba(124,77,255,0.5)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                          <div style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: fileColor(material.filename)[0], color: fileColor(material.filename)[1] }}>
                            <FileIcon name={material.filename} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{material.filename}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{fileMeta(material, material.filename)}</div>
                          </div>
                          <span className="lv-dl" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 11, background: 'var(--primary-light)', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Скачать
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <LessonPager
                ids={lessonIds}
                currentId={id}
                makePath={(lid) => `/lesson/${lid}`}
                fromPath={location.state?.from || '/lessons'}
                prevLabel="Предыдущий урок"
                nextLabel="Следующий урок"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonView;
